import React, { useMemo, useState } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, IconButton, Link, Skeleton, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoNotice from '../../Components/InfoNotice';
import PanelEmptyState from '../../Components/PanelEmptyState';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import { formatMetaDataAsOf } from '../metaAdsConnection';
import { needsMetaReconnect } from '../metaAdsConfig';
import {
  META_MAX_TRACKED_ACCOUNTS,
  describeSelectionChange,
  isInitialLoadPending,
  isSelectableAccount,
  isSelectionDirty,
  selectedCurrencies,
  toggleAccountSelection,
  trackedAccountIds,
} from '../metaAdAccounts';
import { MetaConnectButton } from './MetaConnectStep';
import {
  getMetaAdsStatusPillSx,
  metaAdsBodyTextSx,
  metaAdsCardSx,
  metaAdsIconButtonSx,
  metaAdsMutedTextSx,
  metaAdsPrimaryButtonSx,
  metaAdsRowOpenLinkSx,
  metaAdsSkeletonSx,
  metaAdsStickyFooterSx,
} from './metaAdsUi.styles';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const textButtonSx = { textTransform: 'none', fontWeight: 600, alignSelf: 'flex-start', px: 1, minWidth: 0 };

function AccountMeta({ account }) {
  const parts = [account.currency || 'Moneda sin informar'];
  if (account.activeCampaignsCount > 0) {
    parts.push(plural(account.activeCampaignsCount, 'campaña activa', 'campañas activas'));
  }
  const synced = formatMetaDataAsOf(account.lastSyncedAt);
  if (synced) parts.push(synced);
  // Solo si la API manda el campo (aunque venga null → «—»).
  if (account.opportunity?.known) {
    parts.push(account.opportunity.score == null ? 'Oportunidad —' : `Oportunidad ${account.opportunity.score}/100`);
  }
  return <Typography sx={metaAdsMutedTextSx}>{parts.join(' · ')}</Typography>;
}

function AccountRow({ account, canManage, checked, disabled, onToggle }) {
  const labelId = `meta-ad-account-${account.id}`;
  return (
    <Box
      component="li"
      data-testid={`meta-ad-account-${account.id}`}
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1, borderTop: '1px solid rgba(255,255,255,0.06)', '&:first-of-type': { borderTop: 'none' } }}
    >
      {canManage ? (
        <Checkbox
          size="small"
          checked={checked}
          disabled={disabled}
          onChange={() => onToggle(account.id)}
          inputProps={{ 'aria-labelledby': labelId }}
          sx={{ p: 0.5, mt: '-2px' }}
        />
      ) : null}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography id={labelId} sx={{ ...metaAdsBodyTextSx, fontWeight: 600, overflowWrap: 'anywhere' }}>{account.name}</Typography>
          {!account.isAvailable ? <Box component="span" sx={getMetaAdsStatusPillSx('warning')}>Ya no la compartís</Box> : null}
          {account.isAvailable && account.statusLabel ? (
            <Box component="span" sx={getMetaAdsStatusPillSx('warning')}>{account.statusLabel}</Box>
          ) : null}
          {account.metaUrl ? (
            // «Ver» a la altura del nombre, en el vértice derecho. El nombre accesible sigue diciendo a dónde va.
            <Link
              href={account.metaUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              aria-label={`Ver en Meta: ${account.name}`}
              sx={metaAdsRowOpenLinkSx}
            >
              Ver
              <OpenInNewIcon sx={{ fontSize: 14 }} aria-hidden />
            </Link>
          ) : null}
        </Box>
        <AccountMeta account={account} />
        {!account.isAvailable && account.isTracked ? (
          <Typography sx={metaAdsMutedTextSx}>
            Ya no compartida en Meta — sin datos nuevos. Su historial se sigue viendo mientras la sigas.
          </Typography>
        ) : null}
        {!account.isAvailable && !account.isTracked ? (
          <Typography sx={metaAdsMutedTextSx}>
            Dejaste de compartirla con Terrio en Meta. Para volver a seguirla, reconectá y marcala en la ventana de Meta.
          </Typography>
        ) : null}
        {isInitialLoadPending(account) ? (
          <Typography sx={metaAdsMutedTextSx}>Falta traer sus últimos 90 días.</Typography>
        ) : null}
        {account.isAvailable && account.statusLabel ? (
          <Typography sx={metaAdsMutedTextSx}>Revisala en el Administrador de anuncios de Meta.</Typography>
        ) : null}
      </Box>
    </Box>
  );
}

export function MetaAdAccountsSkeleton() {
  return (
    <Box sx={metaAdsCardSx} data-testid="meta-ad-accounts-skeleton" aria-busy="true">
      {[0, 1].map((i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1 }}>
          <Skeleton variant="rounded" width={20} height={20} sx={metaAdsSkeletonSx} />
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Skeleton variant="rounded" width="60%" height={18} sx={metaAdsSkeletonSx} />
            <Skeleton variant="rounded" width="40%" height={14} sx={metaAdsSkeletonSx} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Vista «Cuentas publicitarias» del panel Meta Ads (E2). El dueño
 * (`canManage`) elige hasta 20 y guarda la lista exacta; el editor ve las
 * seguidas en solo lectura.
 *
 * @param {{ canManage: boolean, accountsState: object, saving: boolean,
 *   saveError?: object|null, onSave: (ids: string[]) => void, connecting: boolean,
 *   onReconnect: () => void }} props
 */
export default function MetaAdAccountsView({
  canManage,
  accountsState,
  saving,
  saveError = null,
  onSave,
  connecting,
  onReconnect,
}) {
  const { loaded, loading, refreshing, accounts, error, errorCode, reload, refreshFromMeta } = accountsState;
  // Cada lista nueva (carga, «Actualizar lista», guardado) vuelve a lo seguido.
  // Se ajusta en el render (no en un efecto) para no pintar un frame con la selección vieja.
  const [selection, setSelection] = useState(() => ({ source: accounts, ids: trackedAccountIds(accounts) }));
  let selected = selection.ids;
  if (selection.source !== accounts) {
    selected = trackedAccountIds(accounts);
    setSelection({ source: accounts, ids: selected });
  }
  const setSelected = (update) => setSelection((prev) => ({ ...prev, ids: update(prev.ids) }));

  const change = useMemo(() => describeSelectionChange(selected, accounts), [selected, accounts]);
  const currencies = useMemo(() => selectedCurrencies(selected, accounts), [selected, accounts]);
  const dirty = isSelectionDirty(selected, accounts);
  const atMax = selected.length >= META_MAX_TRACKED_ACCOUNTS;
  const reconnectError = needsMetaReconnect({ code: errorCode }) || needsMetaReconnect(saveError);

  if (!loaded || loading) return <MetaAdAccountsSkeleton />;

  if (error && accounts.length === 0 && !reconnectError) {
    return (
      <PanelEmptyState
        icon={CampaignOutlinedIcon}
        title="No pudimos cargar las cuentas"
        hint={error}
        actionLabel="Reintentar"
        actionIcon={RefreshIcon}
        onAction={reload}
        testId="meta-ad-accounts-error"
      />
    );
  }

  const reconnectNotice = reconnectError && canManage ? (
    <InfoNotice
      title="Hay que volver a conectar Meta"
      description={error || 'Reconectá para leer tus cuentas publicitarias.'}
      actionLabel="Reconectar"
      onAction={onReconnect}
      actionDisabled={connecting}
      data-testid="meta-ad-accounts-reconnect"
    />
  ) : null;

  // ── Editor: solo lectura, solo las seguidas ───────────────────────────
  if (!canManage) {
    const tracked = accounts.filter((a) => a.isTracked);
    return (
      <>
        <InfoNotice
          title="Solo el dueño del espacio puede cambiar las cuentas"
          description="Si falta una, avisale para que la elija."
          data-testid="meta-ad-accounts-editor-notice"
        />
        {tracked.length === 0 ? (
          <Typography sx={metaAdsMutedTextSx} data-testid="meta-ad-accounts-editor-empty">
            El dueño todavía no eligió cuentas publicitarias.
          </Typography>
        ) : (
          <Box sx={metaAdsCardSx}>
            <Typography sx={metaAdsMutedTextSx}>{plural(tracked.length, 'cuenta seguida', 'cuentas seguidas')}</Typography>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {tracked.map((a) => <AccountRow key={a.id} account={a} canManage={false} />)}
            </Box>
          </Box>
        )}
      </>
    );
  }

  // ── Dueño ─────────────────────────────────────────────────────────────
  const refreshButton = (
    <Button
      size="small"
      onClick={refreshFromMeta}
      disabled={refreshing || saving}
      startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
      sx={textButtonSx}
    >
      {refreshing ? 'Actualizando lista…' : 'Actualizar lista'}
    </Button>
  );
  // Arriba a la derecha: solo la flecha (con texto se partía en dos líneas). Tooltip de
  // MUI con nodo (sin `title` nativo); el `aria-label` da el nombre accesible. El
  // `span` deja mostrar el tooltip aunque el botón esté deshabilitado.
  const refreshIconButton = (
    <Tooltip title={<span>Actualizar lista</span>} placement="top" arrow>
      <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
        <IconButton
          size="small"
          onClick={refreshFromMeta}
          disabled={refreshing || saving}
          aria-label="Actualizar lista"
          aria-busy={refreshing || undefined}
          sx={metaAdsIconButtonSx}
        >
          {refreshing
            ? <CircularProgress size={16} color="inherit" data-testid="meta-ad-accounts-refreshing" />
            : <RefreshIcon sx={{ fontSize: 20 }} />}
        </IconButton>
      </Box>
    </Tooltip>
  );

  const selectable = accounts.filter(isSelectableAccount);
  if (selectable.length === 0) {
    return (
      <>
        {reconnectNotice}
        <Box sx={metaAdsCardSx} data-testid="meta-ad-accounts-empty">
          <Typography sx={metaAdsBodyTextSx}>
            Meta no nos compartió ninguna cuenta publicitaria.
          </Typography>
          <Typography sx={metaAdsMutedTextSx}>
            Volvé a conectar y, en la ventana de Meta, marcá la cuenta que querés usar. Si la acabás de crear, actualizá la lista.
          </Typography>
          {error ? <Typography sx={metaAdsMutedTextSx} role="alert">{error}</Typography> : null}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <MetaConnectButton label="Volver a conectar" connecting={connecting} onConnect={onReconnect} />
            {refreshButton}
          </Box>
        </Box>
        {accounts.length > 0 ? (
          <Box component="ul" sx={{ ...metaAdsCardSx, listStyle: 'none', m: 0 }}>
            {accounts.map((a) => <AccountRow key={a.id} account={a} canManage checked={false} disabled onToggle={() => {}} />)}
          </Box>
        ) : null}
      </>
    );
  }

  return (
    <>
      {reconnectNotice}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={metaAdsMutedTextSx}>
          Elegí qué cuentas seguir. Mostramos los montos en la moneda de cada una.
        </Typography>
        {refreshIconButton}
      </Box>
      {error && !reconnectError ? <Typography sx={metaAdsMutedTextSx} role="alert">{error}</Typography> : null}
      <Box component="ul" sx={{ ...metaAdsCardSx, listStyle: 'none', m: 0, gap: 0 }} data-testid="meta-ad-accounts-list">
        {accounts.map((a) => {
          const checked = selected.includes(a.id);
          return (
            <AccountRow
              key={a.id}
              account={a}
              canManage
              checked={checked}
              disabled={saving || !isSelectableAccount(a) || (!checked && atMax)}
              onToggle={(id) => setSelected((prev) => toggleAccountSelection(prev, id))}
            />
          );
        })}
      </Box>

      {atMax ? (
        <Typography sx={metaAdsMutedTextSx} data-testid="meta-ad-accounts-max">
          Llegaste al máximo de {META_MAX_TRACKED_ACCOUNTS} cuentas. Sacá una para elegir otra.
        </Typography>
      ) : null}
      {currencies.length > 1 ? (
        <Typography sx={metaAdsMutedTextSx} data-testid="meta-ad-accounts-currencies">
          Elegiste cuentas en {currencies.join(' y ')}: los montos se muestran por moneda, sin sumarlos.
        </Typography>
      ) : null}
      {change.removedWithData.length > 0 ? (
        <InfoNotice
          title={change.removedWithData.length === 1
            ? `Vas a dejar de seguir «${change.removedWithData[0].name}»`
            : `Vas a dejar de seguir ${change.removedWithData.length} cuentas con datos`}
          description="Dejás de ver sus campañas y resultados acá. No se borran sus datos ni sus vínculos: si la volvés a elegir, vuelven y traemos de nuevo sus últimos 90 días."
          data-testid="meta-ad-accounts-removed-note"
        />
      ) : null}
      {change.unavailableTracked.length > 0 ? (
        <InfoNotice
          title={change.unavailableTracked.length === 1
            ? `«${change.unavailableTracked[0].name}» ya no está compartida en Meta`
            : `${change.unavailableTracked.length} cuentas ya no están compartidas en Meta`}
          description="Si la dejás de seguir, no la vas a poder volver a sumar hasta que la compartas de nuevo con Terrio en Meta."
          data-testid="meta-ad-accounts-unavailable-note"
        />
      ) : null}

      <Box sx={metaAdsStickyFooterSx}>
        <Typography sx={metaAdsMutedTextSx} data-testid="meta-ad-accounts-count">
          {selected.length === 1 ? '1 elegida' : `${selected.length} elegidas`}
        </Typography>
        <Button
          variant="contained"
          disableElevation
          onClick={() => onSave(selected)}
          disabled={!dirty || saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          sx={metaAdsPrimaryButtonSx}
        >
          {saving ? 'Guardando…' : 'Seguir estas cuentas'}
        </Button>
      </Box>
    </>
  );
}
